import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { GAME_LABELS, GAME_XP } from '../hooks/useGameChallenge';
import ChallengeResultOverlay from './ChallengeResultOverlay';
import Icon from './Icon';

const Avatar = ({ u, size = 10, highlight }) => {
  const cls = `rounded-full overflow-hidden border-2 shrink-0 flex-none transition-all ${
    highlight ? 'border-f1red shadow-[0_0_12px_rgba(225,6,0,.5)] scale-105' : 'border-zinc-700'
  }`;
  const dim = `w-${size} h-${size}`;
  return (
    <div className={`${cls} ${dim}`}>
      {u?.avatar
        ? <img src={u.avatar} alt="" className="w-full h-full object-cover" />
        : <div className="w-full h-full flex items-center justify-center text-sm font-black"
            style={{ background: highlight ? 'rgba(225,6,0,.25)' : 'rgba(255,255,255,.06)', color: highlight ? '#e10600' : '#a1a1aa' }}>
            {u?.username?.[0]?.toUpperCase() ?? '?'}
          </div>
      }
    </div>
  );
};

const ScoreBox = ({ score, pending, winner, loser, submitted }) => {
  const borderColor = winner ? 'rgba(234,179,8,.5)' : loser ? 'rgba(225,6,0,.3)' : 'rgba(255,255,255,.08)';
  const bg          = winner ? 'rgba(234,179,8,.08)' : loser ? 'rgba(225,6,0,.04)' : 'rgba(255,255,255,.03)';
  const textColor   = winner ? '#EAB308' : loser ? '#52525b' : pending ? '#3f3f46' : '#ffffff';

  return (
    <div className="flex flex-col items-center gap-1 flex-1">
      <div className="w-full rounded-xl px-3 py-3 text-center transition-all"
        style={{ border: `1px solid ${borderColor}`, background: bg }}>
        <p className="text-xl font-black tabular-nums" style={{ color: textColor }}>
          {pending ? '—' : (score ?? '—')}
        </p>
        {winner   && <p className="text-yellow-400 text-[9px] font-black uppercase tracking-wider mt-0.5">Gagnant</p>}
        {loser    && <p className="text-red-500 text-[9px] font-black uppercase tracking-wider mt-0.5">Perdant</p>}
        {!winner && !loser && submitted && <p className="text-zinc-500 text-[9px] mt-0.5">soumis ✓</p>}
        {!winner && !loser && pending   && <p className="text-zinc-600 text-[9px] mt-0.5 animate-pulse">en attente…</p>}
      </div>
    </div>
  );
};

const ChallengeDuelBanner = ({
  activeChallenge,
  opponent,
  myScore,
  opponentScore,
  hasSubmitted,
  isCompleted,
  iWon,
  myLiveScore,
  canSubmit,
  formatScore,
  onSubmit,
  submitting,
  game,
}) => {
  const { user } = useAuth();
  const [showOverlay, setShowOverlay] = useState(false);
  const prevCompleted = useRef(isCompleted);

  useEffect(() => {
    if (!prevCompleted.current && isCompleted && activeChallenge?._id) {
      const key = `overlay_seen_${activeChallenge._id}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        setShowOverlay(true);
      }
    }
    prevCompleted.current = isCompleted;
  }, [isCompleted, activeChallenge?._id]);

  if (!activeChallenge || !opponent) return null;

  const label   = GAME_LABELS[game] ?? game;
  const xp      = GAME_XP[game] ?? 1000;
  const isDraw  = isCompleted && !activeChallenge.winner;

  const myDisplayScore  = hasSubmitted && myScore !== null ? formatScore(myScore)
                        : (myLiveScore !== null && myLiveScore !== undefined ? formatScore(myLiveScore) : null);
  const oppDisplayScore = opponentScore !== null ? formatScore(opponentScore) : null;

  const meWon  = iWon;
  const meLost = isCompleted && !iWon && !isDraw;

  return (
    <>
      <style>{`
        @keyframes db-in{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes db-pulse{0%,100%{box-shadow:0 0 0 0 rgba(225,6,0,.25)}50%{box-shadow:0 0 0 6px rgba(225,6,0,.0)}}
      `}</style>
      <div className="mb-5 rounded-2xl overflow-hidden"
        style={{
          border: isCompleted
            ? meWon ? '2px solid rgba(234,179,8,.5)' : '2px solid rgba(225,6,0,.3)'
            : '2px solid rgba(225,6,0,.35)',
          background: isCompleted
            ? meWon ? 'linear-gradient(135deg,rgba(234,179,8,.06) 0%,#0c0c10 60%)' : 'linear-gradient(135deg,rgba(225,6,0,.04) 0%,#0c0c10 60%)'
            : 'linear-gradient(135deg,rgba(225,6,0,.06) 0%,#0c0c10 60%)',
          boxShadow: isCompleted
            ? meWon ? '0 0 30px rgba(234,179,8,.12)' : 'none'
            : '0 0 20px rgba(225,6,0,.08)',
          animation: 'db-in .35s ease both',
        }}>

        {/* Top stripe */}
        <div style={{
          height: 3,
          background: isCompleted
            ? meWon ? 'linear-gradient(90deg,#EAB308 0%,transparent 70%)' : 'linear-gradient(90deg,#e10600 0%,transparent 70%)'
            : 'linear-gradient(90deg,#e10600 0%,transparent 70%)',
        }} />

        <div className="px-4 py-4">
          {/* Header row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="sword" size={16} />
              <div>
                <p className="text-white font-black text-sm leading-tight">
                  {isCompleted
                    ? meWon ? 'Victoire !' : isDraw ? 'Égalité !' : 'Défaite'
                    : 'Défi en cours'}
                </p>
                <p className="text-zinc-500 text-[10px]">{label}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-black uppercase tracking-wider"
                style={{ color: isCompleted && meWon ? '#EAB308' : '#e10600' }}>
                {isCompleted && meWon ? `+${xp} XP` : `${xp} XP en jeu`}
              </p>
              {!isCompleted && (
                <p className="text-[9px] text-zinc-600">
                  {hasSubmitted ? '✓ Score soumis' : 'Score non soumis'}
                </p>
              )}
            </div>
          </div>

          {/* VS row */}
          <div className="flex items-center gap-3">
            {/* Me */}
            <div className="flex flex-col items-center gap-2 flex-1">
              <Avatar u={user} size={10} highlight={meWon} />
              <p className="text-white text-xs font-black truncate max-w-full">{user?.username}</p>
            </div>

            {/* Scores */}
            <div className="flex items-stretch gap-2 flex-[2]">
              <ScoreBox
                score={myDisplayScore}
                label={hasSubmitted ? 'soumis' : myLiveScore !== null && myLiveScore !== undefined ? 'live' : ''}
                pending={!myDisplayScore}
                winner={isCompleted && meWon}
                loser={isCompleted && meLost}
                submitted={hasSubmitted}
              />
              <div className="flex items-center justify-center shrink-0">
                <span className="text-zinc-700 font-black text-lg">VS</span>
              </div>
              <ScoreBox
                score={oppDisplayScore}
                label={opponentScore !== null ? 'soumis' : ''}
                pending={opponentScore === null}
                winner={isCompleted && !meWon && !isDraw}
                loser={isCompleted && meWon}
                submitted={opponentScore !== null}
              />
            </div>

            {/* Opponent */}
            <div className="flex flex-col items-center gap-2 flex-1">
              <Avatar u={opponent} size={10} highlight={isCompleted && !meWon && !isDraw} />
              <p className="text-zinc-300 text-xs font-black truncate max-w-full">{opponent?.username}</p>
            </div>
          </div>

          {/* Submit button */}
          {!hasSubmitted && !isCompleted && canSubmit && (
            <button onClick={onSubmit} disabled={submitting}
              className="mt-4 w-full py-2.5 rounded-xl font-black text-sm text-white disabled:opacity-50 transition-all hover:opacity-90"
              style={{
                background: 'linear-gradient(135deg,#e10600 0%,#b30500 100%)',
                boxShadow: '0 4px 16px rgba(225,6,0,.35)',
                animation: 'db-pulse 2s infinite',
              }}>
              {submitting ? '…' : `Soumettre mon score contre ${opponent?.username}`}
            </button>
          )}

          {hasSubmitted && !isCompleted && (
            <p className="mt-3 text-center text-zinc-500 text-xs animate-pulse">
              En attente du score de {opponent?.username}…
            </p>
          )}
        </div>
      </div>

      {showOverlay && (
        <ChallengeResultOverlay
          iWon={iWon}
          isDraw={isDraw}
          me={user}
          opponent={opponent}
          myScore={myScore}
          opponentScore={opponentScore}
          formatScore={formatScore}
          game={game}
          onClose={() => setShowOverlay(false)}
        />
      )}
    </>
  );
};

export default ChallengeDuelBanner;
